import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DialogAction } from '../../dialog-action';
import { DialogContext } from '../../dialog-data';
import { DashboardDialogFramePart } from '../../dialog-frame/dialog-frame.part';


@Component({
	selector: 'dashboard-edit-text-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
	],
	templateUrl: './edit-text.dialog.html',
	styleUrls: ['./edit-text.dialog.scss']
})
export class EditTextDialog {
	//
	// Configuration for the input fields 
	//
	text = {
		value: '',
		changed: false,
		pending: '',
		valid: true,
	};

	readonly: boolean;
	header = 'Text';

	saveEnabled = signal(false);

	//
	// The action buttons
	//
	saveAction: DialogAction<{ text: string }> = {
		id: 'save',
		cssClass: signal(undefined),
		enabled: this.saveEnabled,
		visible: signal(true),
		text: signal('Save'),
		linkType: 'callback',
		callback: async () => ({
			text: this.text.pending,
		}),
		willCloseDialog: true,
	};

	closeAction: DialogAction = {
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: "no-action",
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.saveAction, this.closeAction];
	private dialog: DialogContext<{ text: string, readonly: boolean, header: string, }> = inject(MAT_DIALOG_DATA);

	constructor(
		public dialogRef: MatDialogRef<EditTextDialog>,
		private util: UtilityService,
	) {
		this.readonly = this.dialog.data?.readonly ?? true;
		this.header = this.dialog.data?.header ?? 'Text';

		if (this.dialog.data?.text) {
			this.text.pending = this.dialog.data.text;
			this.text.value = this.dialog.data.text;
		}

		if (this.readonly) this.saveEnabled.set(false);
	}


	/**
	 * Record the pending value and check if it is valid (within the length range)
	 */
	setPendingValue(text: string) {

		//
		// Cleanup the text
		//
		text = (text ?? '').trim();

		//
		// Titles can be three-letter, uppercase (e.g. CEO) but anything else has the case fixed
		//
		text = this.util.text.fixCase(text, 'sentence-case');


		const values = this.text;
		values.pending = text;
		values.valid = values.pending != values.value;

		this.checkIfCanSave();
	}


	/**
	 * This function is called as the user is typing/pasting/deleting into a field
	 */
	onInput(event: Event) {
		const textarea = event.target as HTMLTextAreaElement;
		const text = (textarea.value ?? '').trim();
		this.setPendingValue(text);
		this.checkIfCanSave();
	}

	/**
	 * When losing focus and a pending values was added, that pending value may have been
	 * cleaned (e.g. fixCase) so we update the value with the pending value so it is bound
	 * back into the input field and shown to the user. 
	 */
	onBlur() {
		this.text.changed = true;
		if (this.text.value !== this.text.pending) {
			this.text.value = this.text.pending;
			this.checkIfCanSave();
		}
	}

	/**
	 * Look at each value and determine if the save/update button should be enabled
	 */
	checkIfCanSave() {
		this.saveEnabled.set(!this.readonly && this.text.valid);
	}

}